import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosApi } from "../../helpers/AxiosApi";
import { toast } from "react-toastify";
import initialStates from "./state";

// Get Sales Report
export const getSalesReport = createAsyncThunk(
  "getSalesReport",
  async (
    {
      year,
      type,
      status,
      start_date,
      end_date,
      sort_by,
      sort_direction,
      pageLimit,
      pageCount,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await AxiosApi.get(
        `sales-report?year=${year}&subscription_type=${type}&status=${status}&start_date=${start_date}&end_date=${end_date}&sort_by=${sort_by}&sort_direction=${sort_direction}&page=${pageCount}&limit=${pageLimit}`
      );
      return response.data.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);
// Get Sales Report
export const getServiceRequestReport = createAsyncThunk(
  "getServiceRequestReport",
  async (
    {
      year,
      status,
      start_date,
      end_date,
      pageLimit,
      pageCount,
      service_id,
      sort_by,
      sort_direction,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await AxiosApi.get(
        `service-request-report?year=${year}&status=${status}&sort_by=${sort_by}&sort_direction=${sort_direction}&start_date=${start_date}&end_date=${end_date}&page=${pageCount}&limit=${pageLimit}&service_id=${service_id}`
      );
      return response.data.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

// Get subscription Report
export const getSubscriptionReport = createAsyncThunk(
  "getSubscriptionReport",
  async (
    {
      year,
      status,
      start_date,
      end_date,
      pageLimit,
      pageCount,
      subscription_type,
      sort_by,
      sort_direction,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await AxiosApi.get(
        `subscription-report?year=${year}&status=${status}&start_date=${start_date}&sort_by=${sort_by}&sort_direction=${sort_direction}&end_date=${end_date}&page=${pageCount}&limit=${pageLimit}&subscription_type=${subscription_type}`
      );
      return response.data.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

// Get Ticket Report
export const getTicketReport = createAsyncThunk(
  "getTicketReport",
  async (
    {
      year,
      start_date,
      end_date,
      pageLimit,
      pageCount,
      sort_by,
      sort_direction,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await AxiosApi.get(
        `ticket-report?year=${year}&start_date=${start_date}&end_date=${end_date}&sort_by=${sort_by}&sort_direction=${sort_direction}&page=${pageCount}&limit=${pageLimit}`
      );
      return response.data.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

// Get Business Activities
export const getBusinessActivities = createAsyncThunk(
  "getBusinessActivities",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosApi.get(`business-activities`);
      return response.data.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

// Get Company Types Report
export const getCompanyTypesReport = createAsyncThunk(
  "getCompanyTypesReport",
  async (
    { id, sort_by, sort_direction, pageLimit, pageCount },
    { rejectWithValue }
  ) => {
    try {
      const response = await AxiosApi.get(
        `company-types-report?ssic_activities_id=${id}&sort_by=${sort_by}&sort_direction=${sort_direction}&page=${pageCount}&limit=${pageLimit}`
      );
      return response.data.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);
// Get Companies Report
export const getCompaniesReport = createAsyncThunk(
  "getCompaniesReport",
  async (
    { id, sort_by, sort_direction, pageLimit, pageCount },
    { rejectWithValue }
  ) => {
    try {
      const response = await AxiosApi.get(
        `companies-report?ssic_activities_id=${id}&sort_by=${sort_by}&sort_direction=${sort_direction}&page=${pageCount}&limit=${pageLimit}`
      );
      return response.data.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

const reportSlice = createSlice({
  name: "report",
  initialState: initialStates,
  extraReducers: (builder) => {
    builder
      // Get Sales Report
      .addCase(getSalesReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSalesReport.fulfilled, (state, action) => {
        state.loading = false;
        state.getSalesReportStatus.data = action.payload;
        state.error = false;
      })
      .addCase(getSalesReport.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      // Get Service Request Report
      .addCase(getServiceRequestReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(getServiceRequestReport.fulfilled, (state, action) => {
        state.loading = false;
        state.getServiceRequestReportStatus.data = action.payload;
        state.error = false;
      })
      .addCase(getServiceRequestReport.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      // Get Subscription Report
      .addCase(getSubscriptionReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSubscriptionReport.fulfilled, (state, action) => {
        state.loading = false;
        state.getSubscriptionReportStatus.data = action.payload;
        state.error = false;
      })
      .addCase(getSubscriptionReport.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      // Get Ticket Report
      .addCase(getTicketReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTicketReport.fulfilled, (state, action) => {
        state.loading = false;
        state.getTicketReportStatus.data = action.payload;
        state.error = false;
      })
      .addCase(getTicketReport.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      // Get Business Activities
      .addCase(getBusinessActivities.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBusinessActivities.fulfilled, (state, action) => {
        state.loading = false;
        state.getBusinessActivitiesStatus.data = action.payload;
        state.error = false;
      })
      .addCase(getBusinessActivities.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      // Get Company Types Report
      .addCase(getCompanyTypesReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCompanyTypesReport.fulfilled, (state, action) => {
        state.loading = false;
        state.getCompanyTypesReportStatus.data = action.payload;
        state.error = false;
      })
      .addCase(getCompanyTypesReport.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      // get Companies Report
      .addCase(getCompaniesReport.pending, (state) => {
        state.getCompaniesReportStatus.loading = true;
      })
      .addCase(getCompaniesReport.fulfilled, (state, action) => {
        state.getCompaniesReportStatus.loading = false;
        state.getCompaniesReportStatus.data = action.payload;
        state.error = false;
      })
      .addCase(getCompaniesReport.rejected, (state) => {
        state.getCompaniesReportStatus.loading = false;
        state.error = true;
      });
  },
});

const { reducer } = reportSlice;
export default reducer;
